import { useCallback, useRef } from 'react';
import styles from './ColorPicker.module.css';

const clamp = (val: number, min: number, max: number) =>
  Math.min(Math.max(val, min), max);

interface SaturationPickerProps {
  variant: 'saturation';
  hueColor: string;
  saturation: number;
  brightness: number;
  indicatorColor: string;
  onChange: (saturation: number, brightness: number) => void;
  className?: string;
}

interface HuePickerProps {
  variant: 'hue';
  value: number;
  indicatorColor: string;
  onChange: (hue: number) => void;
  className?: string;
}

interface OpacityPickerProps {
  variant: 'opacity';
  value: number;
  color: string;
  indicatorColor: string;
  onChange: (opacity: number) => void;
  className?: string;
}

export interface GradientStop {
  id: string;
  position: number;
  color: string;
  opacity?: number;
}

interface GradientPickerProps {
  variant: 'gradient';
  stops: GradientStop[];
  selectedStopId?: string;
  onChange: (stops: GradientStop[]) => void;
  onSelectStop?: (id: string) => void;
  onAddStop?: (position: number) => void;
  onRemoveStop?: (id: string) => void;
  className?: string;
}

type ColorPickerProps =
  | SaturationPickerProps
  | HuePickerProps
  | OpacityPickerProps
  | GradientPickerProps;

const SaturationPicker = ({
  hueColor,
  saturation,
  brightness,
  indicatorColor,
  onChange,
  className = '',
}: Omit<SaturationPickerProps, 'variant'>) => {
  const ref = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;

    const update = (cx: number, cy: number) => {
      const rect = el.getBoundingClientRect();
      const s = clamp((cx - rect.left) / rect.width, 0, 1);
      const v = clamp(1 - (cy - rect.top) / rect.height, 0, 1);
      onChangeRef.current(s, v);
    };

    update(e.clientX, e.clientY);

    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      update(ev.clientX, ev.clientY);
    };
    const onUp = (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', onUp, true);
    };
    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', onUp, true);
  };

  return (
    <div
      ref={ref}
      className={`${styles.picker} ${styles.pickerSaturation} ${className}`}
      style={{ backgroundColor: hueColor }}
      onPointerDown={handleDown}
    >
      <div className={styles.saturationWhite} />
      <div className={styles.saturationBlack} />
      <div
        className={styles.indicator}
        style={{
          left: `${saturation * 100}%`,
          top: `${(1 - brightness) * 100}%`,
          backgroundColor: indicatorColor,
        }}
      />
    </div>
  );
};

const HuePicker = ({
  value,
  indicatorColor,
  onChange,
  className = '',
}: Omit<HuePickerProps, 'variant'>) => {
  const ref = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;

    const update = (cx: number) => {
      const rect = el.getBoundingClientRect();
      const h = clamp((cx - rect.left) / rect.width, 0, 1) * 360;
      onChangeRef.current(h);
    };

    update(e.clientX);

    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      update(ev.clientX);
    };
    const onUp = (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', onUp, true);
    };
    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', onUp, true);
  };

  return (
    <div className={`${styles.sliderWrapper} ${className}`}>
      <div
        ref={ref}
        className={`${styles.picker} ${styles.pickerHue}`}
        onPointerDown={handleDown}
      >
        <div
          className={styles.sliderIndicator}
          style={{
            left: `${(value / 360) * 100}%`,
            backgroundColor: indicatorColor,
          }}
        />
      </div>
    </div>
  );
};

const OpacityPicker = ({
  value,
  color,
  indicatorColor,
  onChange,
  className = '',
}: Omit<OpacityPickerProps, 'variant'>) => {
  const ref = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;

    const update = (cx: number) => {
      const rect = el.getBoundingClientRect();
      const val = Math.round(clamp((cx - rect.left) / rect.width, 0, 1) * 100);
      onChangeRef.current(val);
    };

    update(e.clientX);

    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      update(ev.clientX);
    };
    const onUp = (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', onUp, true);
    };
    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', onUp, true);
  };

  return (
    <div className={`${styles.sliderWrapper} ${className}`}>
      <div
        ref={ref}
        className={`${styles.picker} ${styles.pickerOpacity}`}
        onPointerDown={handleDown}
      >
        <div className={styles.opacityBg} />
        <div
          className={styles.opacityGradient}
          style={{ background: `linear-gradient(to right, transparent, ${color})` }}
        />
        <div
          className={styles.sliderIndicator}
          style={{
            left: `${value}%`,
            backgroundColor: indicatorColor,
          }}
        />
      </div>
    </div>
  );
};

const GradientPicker = ({
  stops,
  selectedStopId,
  onChange,
  onSelectStop,
  onAddStop,
  onRemoveStop,
  className = '',
}: Omit<GradientPickerProps, 'variant'>) => {
  const ref = useRef<HTMLDivElement>(null);
  const draggingId = useRef<string | null>(null);

  const sorted = [...stops].sort((a, b) => a.position - b.position);

  const toRgba = (hex: string, op: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${op / 100})`;
  };

  const gradientCss = sorted
    .map((s) => `${toRgba(s.color, s.opacity ?? 100)} ${s.position}%`)
    .join(', ');

  const getPosition = useCallback(
    (clientX: number) => {
      if (!ref.current) return 0;
      const rect = ref.current.getBoundingClientRect();
      return Math.round(clamp((clientX - rect.left) / rect.width, 0, 1) * 100);
    },
    []
  );

  const handleStopDown = (e: React.PointerEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    draggingId.current = id;
    onSelectStop?.(id);
    ref.current?.focus();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handleStopMove = (e: React.PointerEvent) => {
    if (!draggingId.current || e.buttons === 0) return;
    const pos = getPosition(e.clientX);
    const updated = stops.map((s) =>
      s.id === draggingId.current ? { ...s, position: pos } : s
    );
    onChange(updated);
  };

  const handleStopUp = () => {
    draggingId.current = null;
  };

  const handleTrackClick = (e: React.MouseEvent) => {
    if (draggingId.current) return;
    const target = e.target as HTMLElement;
    if (target.closest(`.${styles.gradientStop}`)) return;
    const pos = getPosition(e.clientX);
    onAddStop?.(pos);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Backspace' || e.key === 'Delete') && selectedStopId && stops.length > 2) {
      e.preventDefault();
      onRemoveStop?.(selectedStopId);
    }
  };

  return (
    <div className={`${styles.sliderWrapper} ${className}`}>
      <div
        ref={ref}
        tabIndex={0}
        className={`${styles.picker} ${styles.pickerGradient}`}
        onClick={handleTrackClick}
        onPointerMove={handleStopMove}
        onPointerUp={handleStopUp}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.gradientBg} />
        <div
          className={styles.gradientFill}
          style={{ background: `linear-gradient(to right, ${gradientCss})` }}
        />
        {stops.map((stop) => (
          <div
            key={stop.id}
            className={`${styles.gradientStop} ${stop.id === selectedStopId ? styles.gradientStopSelected : ''}`}
            style={{
              left: `${stop.position}%`,
              backgroundColor: toRgba(stop.color, stop.opacity ?? 100),
            }}
            onPointerDown={(e) => handleStopDown(e, stop.id)}
          />
        ))}
      </div>
    </div>
  );
};

export const ColorPicker = (props: ColorPickerProps) => {
  const { variant, ...rest } = props;

  switch (variant) {
    case 'saturation':
      return <SaturationPicker {...(rest as Omit<SaturationPickerProps, 'variant'>)} />;
    case 'hue':
      return <HuePicker {...(rest as Omit<HuePickerProps, 'variant'>)} />;
    case 'opacity':
      return <OpacityPicker {...(rest as Omit<OpacityPickerProps, 'variant'>)} />;
    case 'gradient':
      return <GradientPicker {...(rest as Omit<GradientPickerProps, 'variant'>)} />;
    default:
      return null;
  }
};

ColorPicker.displayName = 'ColorPicker';
